/**
 * Hit, block, throw, armor and parry resolution. Three-free.
 *
 * Called once per tick per attacker by the combat system, AFTER physics has integrated
 * positions and rebuilt volumes. Both directions are evaluated before either is applied, so a
 * simultaneous exchange resolves as a genuine trade rather than "whoever the loop reached first".
 */

import { HITSTOP, f60, signNonZero } from '../core/index.js';
import { S } from './states.js';
import { enterState, beginKnockdown, applyParrySuccess } from './state-machine.js';
import { refreshDerived, logAction } from './fighter-state.js';
import {
  computeHitDamage, applyComboProgress, computeChipDamage, computeGuardDrain,
  computeHitstun, computeHitstop, computeKnockback, computeMeterGain, addMeter,
} from './damage.js';

/** Pending interaction records, preallocated: at most one per attacker per tick. */
function createPending() {
  return {
    active: false,
    kind: '', // 'hit' | 'block' | 'throw' | 'armor' | 'parry'
    attacker: 0,
    defender: 0,
    moveId: '',
    sourceIndex: 0,
    region: 0,
    cx: 0, cy: 0, cz: 0,
    nx: 0, ny: 0, nz: 0,
    counter: false,
    punish: false,
    airborne: false,
  };
}

export class HitResolver {
  constructor() {
    this.pending = [createPending(), createPending()];
  }

  /**
   * Phase A — detect. Fills `this.pending` without mutating fighter state, so a trade sees
   * both fighters as they were at the start of the exchange.
   */
  detect(ctx, physics) {
    for (let i = 0; i < 2; i++) {
      const p = this.pending[i];
      p.active = false;
      const attacker = ctx.sim.fighters[i];
      const defender = ctx.sim.fighters[1 - i];
      const move = attacker.move;
      if (!move) continue;
      if (attacker.frozenTicks > 0) continue;
      if (attacker.moveHasHit && !move.isThrow) {
        // Multi-hit moves are allowed a second connection only from a different hitbox.
        if (!this._hasUnusedBox(attacker)) continue;
      }

      if (move.isThrow) {
        if (attacker.throwPartner >= 0) continue;
        if (!physics.queryThrow(ctx, i, 1 - i)) continue;
        p.active = true;
        p.kind = 'throw';
        p.attacker = i;
        p.defender = 1 - i;
        p.moveId = move.id;
        const c = physics.lastContact;
        p.cx = c.x; p.cy = c.y; p.cz = c.z;
        p.nx = c.nx; p.ny = c.ny; p.nz = c.nz;
        continue;
      }

      const result = physics.queryHit(ctx, i, 1 - i);
      if (!result) continue;

      p.active = true;
      p.attacker = i;
      p.defender = 1 - i;
      p.moveId = move.id;
      p.sourceIndex = result.sourceIndex;
      p.region = result.region;
      const c = result.contact;
      p.cx = c.x; p.cy = c.y; p.cz = c.z;
      p.nx = c.nx; p.ny = c.ny; p.nz = c.nz;
      p.counter = defender.counterVulnerable;
      p.punish = defender.punishable;
      p.airborne = defender.airborne;

      // Classify the interaction. Order matters: parry beats armor beats guard.
      if (defender.parryTicks > 0 && !defender.airborne && canParry(move)) p.kind = 'parry';
      else if (defender.armorHitsLeft > 0 && move.level !== 'unblockable') p.kind = 'armor';
      else if (canBlock(defender, move)) p.kind = 'block';
      else p.kind = 'hit';
    }
    return this.pending;
  }

  _hasUnusedBox(attacker) {
    const boxes = attacker.move.hitboxes;
    for (let b = 0; b < boxes.length; b++) if (!attacker.hitboxUsed[b]) return true;
    return false;
  }

  /**
   * Phase B — apply. Mutates state. Both records are applied in fighter-index order, which is
   * deterministic and therefore replay-safe.
   */
  apply(ctx, emitter, physics) {
    let maxHitstop = 0;
    for (let i = 0; i < 2; i++) {
      const p = this.pending[i];
      if (!p.active) continue;
      const attacker = ctx.sim.fighters[p.attacker];
      const defender = ctx.sim.fighters[p.defender];
      const move = attacker.move;
      if (!move) continue;

      // The defender may have been knocked out of a throwable state by the other half of a
      // trade in the same tick; re-check the preconditions that matter.
      if (p.kind === 'throw' && !defender.throwable) continue;

      let hitstop = 0;
      switch (p.kind) {
        case 'parry':
          applyParrySuccess(defender, attacker, ctx, emitter);
          attacker.moveHasHit = true;
          attacker.hitboxUsed[p.sourceIndex] = 1;
          hitstop = f60(3);
          break;
        case 'armor':
          hitstop = this._applyArmor(ctx, emitter, attacker, defender, move, p);
          break;
        case 'block':
          hitstop = this._applyBlock(ctx, emitter, attacker, defender, move, p);
          break;
        case 'throw':
          hitstop = this._applyThrow(ctx, emitter, attacker, defender, move, p);
          break;
        default:
          hitstop = this._applyHit(ctx, emitter, attacker, defender, move, p, physics);
          break;
      }
      if (hitstop > maxHitstop) maxHitstop = hitstop;
    }

    // Both fighters freeze for the same duration. That shared freeze is what makes an impact
    // read as a collision instead of two animations passing through each other.
    if (maxHitstop > 0) {
      ctx.sim.hitstop = maxHitstop;
      ctx.sim.fighters[0].frozenTicks = Math.max(ctx.sim.fighters[0].frozenTicks, maxHitstop);
      ctx.sim.fighters[1].frozenTicks = Math.max(ctx.sim.fighters[1].frozenTicks, maxHitstop);
    }
    return maxHitstop;
  }

  // -------------------------------------------------------------------------

  _applyHit(ctx, emitter, attacker, defender, move, p, physics) {
    const hc = {
      counter: p.counter,
      punish: p.punish,
      airborne: p.airborne,
      juggleCount: defender.juggleCount,
    };

    const { damage, rawDamage, scaleUsed, nextScale, counterBonus } =
      computeHitDamage(attacker, move, hc);

    attacker.moveHasHit = true;
    attacker.hitboxUsed[p.sourceIndex] = 1;
    const comboHits = applyComboProgress(attacker, move, damage, nextScale, ctx.time.tick);

    // --- health ---
    const fatal = damage >= defender.health;
    defender.health = Math.max(0, defender.health - damage);
    defender.damageTakenRound += damage;
    attacker.damageDealtRound += damage;
    defender.perfectRound = false;

    // --- meter ---
    if (hc.counter) addMeter(attacker, computeMeterGain(attacker, 'counter-hit'));
    if (hc.punish) addMeter(attacker, computeMeterGain(attacker, 'whiff-punish'));
    addMeter(defender, computeMeterGain(defender, 'damage-taken', damage));

    // --- stun and reaction state ---
    const hitstunTicks = computeHitstun(move, hc);
    defender.hitstunTicks = hitstunTicks;
    defender.blockstunTicks = 0;
    defender.move = null;
    defender.moveId = '';
    defender.parryTicks = 0;
    defender.armorHitsLeft = 0;

    const knockbackX = computeKnockback(defender, move, hc);
    const away = signNonZero(defender.x - attacker.x);
    defender.vx = away * knockbackX;

    // A launcher only LAUNCHES a grounded opponent. Against an already-airborne opponent it acts
    // as a juggle extender instead. Without this, repeatedly connecting a launcher resets the
    // defender's upward velocity before gravity can act on it and carries them off the stage —
    // measured at 86 m before this guard existed.
    const freshLaunch = move.onHit.launch > 0 && !hc.airborne;

    let reaction;
    if (move.onHit.forcesState) {
      reaction = move.onHit.forcesState;
    } else if (freshLaunch) {
      reaction = S.HITSTUN_LAUNCH;
    } else if (defender.airborne) {
      reaction = S.HITSTUN_AIR;
    } else if (hc.counter && move.counterHit.forcesState) {
      reaction = move.counterHit.forcesState;
    } else if (move.level === 'low' || defender.crouching) {
      reaction = S.HITSTUN_CROUCH;
    } else {
      reaction = S.HITSTUN_STAND;
    }

    if (freshLaunch) {
      defender.vy = move.onHit.launch;
      defender.juggleGravityMul = move.onHit.juggleGravityMul * defender.stats.juggleGravityMul;
      defender.juggleCount = 0;
      const lp = emitter.begin('combat:launch');
      lp.attacker = attacker.index;
      lp.defender = defender.index;
      lp.moveId = move.id;
      lp.launchVy = move.onHit.launch;
      lp.comboHits = comboHits;
      emitter.send('combat:launch');
    } else if (defender.airborne) {
      defender.juggleCount++;
      // Each juggle hit lifts slightly less, and the lift shrinks as the juggle gets longer, so
      // every juggle converges to the ground no matter how many extenders connect.
      const decay = 1 / (1 + defender.juggleCount * 0.35);
      defender.vy = Math.max(defender.vy * 0.3 + 2.6 * decay, 0.6);
    }

    enterState(defender, reaction, ctx, emitter, move.id);
    refreshDerived(defender);

    // --- knockdown ---
    if (move.onHit.knockdown !== 'none' && !defender.airborne) {
      defender.headTowardOpponent = false;
      beginKnockdown(defender, ctx, emitter, move.onHit.knockdown === 'hard', true);
    }

    // --- wall splat is decided by physics after the knockback is integrated ---
    if (move.onHit.wallSplat && physics.isCornered(defender) && !defender.wallSplatUsed) {
      defender.wallSplatUsed = true;
      attacker.comboWallUsed = true;
      defender.wallStunTicks = f60(22);
      // Wall stun REPLACES hitstun. Leaving both running lets whichever expires first decide
      // the exit, which is how a splat ends up snapping straight back to idle.
      defender.hitstunTicks = 0;
      enterState(defender, S.WALL_SPLAT, ctx, emitter, move.id);
      const wp = emitter.begin('combat:wall-splat');
      wp.fighter = defender.index;
      wp.wallSide = defender.wallSide;
      wp.impactSpeed = Math.abs(defender.vx);
      wp.x = defender.x;
      wp.y = defender.y + 1.0;
      wp.z = defender.z;
      wp.stunTicks = defender.wallStunTicks;
      emitter.send('combat:wall-splat');
      emitter.cue('wall-impact', defender.x, defender.y + 1.0, defender.z, 1, 1, 0, 'concrete');
      emitter.impact('wall-crack', defender.x, defender.y + 1.05, defender.z,
        -defender.wallSide, 0, 0, 1.2, 'concrete', 1.3);
      emitter.cameraImpulse('wall-splat', 0.85, -defender.wallSide, 0.1, 0, f60(10), 22, 0.03);
    }

    const hitstop = computeHitstop(move, hc, HITSTOP);
    logAction(attacker, ctx.time.tick, move.id, hc.counter ? 'counter' : 'hit');

    // --- events ---
    const hp = emitter.begin('combat:hit');
    hp.attacker = attacker.index;
    hp.defender = defender.index;
    hp.moveId = move.id;
    hp.moveName = move.name;
    hp.damage = damage;
    hp.rawDamage = rawDamage;
    hp.scaling = scaleUsed;
    hp.comboHits = comboHits;
    hp.comboDamage = attacker.comboDamage;
    hp.level = move.level;
    hp.hitstun = move.hitstun;
    hp.hitstop = hitstop;
    hp.x = p.cx;
    hp.y = p.cy;
    hp.z = p.cz;
    hp.nx = p.nx;
    hp.ny = p.ny;
    hp.nz = p.nz;
    hp.launcher = move.onHit.launch > 0;
    hp.counter = hc.counter;
    hp.punish = hc.punish;
    hp.airborne = hc.airborne;
    hp.surface = p.region === 2 ? 'body' : 'body';
    emitter.send('combat:hit');

    if (hc.counter) {
      const cp = emitter.begin('combat:counter');
      cp.attacker = attacker.index;
      cp.defender = defender.index;
      cp.moveId = move.id;
      cp.bonusDamage = counterBonus;
      cp.hitstunBonus = move.counterHit.hitstunBonus;
      emitter.send('combat:counter');
    }

    emitter.damage(defender.index, damage, defender.health, defender.maxHealth, move.id, fatal);
    if (fatal) {
      defender.defeated = true;
      emitter.defeat(defender.index, 'ko');
    }

    // --- presentation ---
    const strength = Math.min(1.6, 0.4 + damage / 34);
    emitter.impact(
      hc.counter ? 'spark-counter' : damage >= 28 ? 'spark-heavy' : damage >= 16 ? 'spark-medium' : 'spark-light',
      p.cx, p.cy, p.cz, p.nx, p.ny, p.nz, strength, 'body', strength,
    );
    emitter.cue(move.sfx.hit || 'impact-light', p.cx, p.cy, p.cz, Math.min(1, 0.6 + damage / 50), 1, 0, 'body');
    emitter.cue(damage >= 24 ? 'exert-heavy' : 'exert-pain', defender.x, defender.y + 1.4, defender.z,
      0.7, defender.id === 'kestrel' ? 1.12 : 0.88, 0);
    emitter.cameraImpulse(
      hc.counter ? 'counter' : damage >= 28 ? 'heavy-impact' : 'impact',
      Math.min(1, 0.22 + damage / 60), p.nx, p.ny * 0.4, p.nz,
      hitstop + f60(3), hc.counter ? 26 : 18, damage >= 28 ? 0.035 : 0.015,
    );

    return hitstop;
  }

  _applyBlock(ctx, emitter, attacker, defender, move, p) {
    attacker.moveHasHit = true;
    attacker.hitboxUsed[p.sourceIndex] = 1;

    const chip = computeChipDamage(defender, move);
    if (chip > 0) {
      defender.health = Math.max(1, defender.health - chip);
      emitter.damage(defender.index, chip, defender.health, defender.maxHealth, move.id, false);
    }

    defender.blockstunTicks = move.tBlockstun;
    defender.hitstunTicks = 0;
    const { guardMeter, guardBreak } = computeGuardDrain(defender, move, move.tBlockstun);
    defender.guardMeter = guardMeter;

    // Pushback: the defender slides back, the attacker slides back less. Cornering the
    // defender converts their pushback into attacker pushback, which is why the wall matters.
    const away = signNonZero(defender.x - attacker.x);
    const cornered = defender.wallSide !== 0 && signNonZero(defender.wallSide) === away;
    if (cornered) {
      attacker.vx = -away * (move.pushbackBlock + move.selfPushbackBlock) * 6;
    } else {
      defender.vx = away * move.pushbackBlock * 7 * defender.stats.cornerPressureMul;
      attacker.vx = -away * move.selfPushbackBlock * 6;
    }

    addMeter(attacker, move.meterGain.block);

    if (guardBreak) {
      // A broken guard is a big punish window, not an instant loss.
      defender.blockstunTicks = 0;
      defender.hitstunTicks = f60(26);
      defender.guardMeter = defender.guardMax * 0.4;
      enterState(defender, S.HITSTUN_STAND, ctx, emitter, move.id);
      emitter.cue('guard-break', p.cx, p.cy, p.cz, 1, 1, 0, 'guard');
      emitter.impact('guard-break', p.cx, p.cy, p.cz, p.nx, p.ny, p.nz, 1.3, 'guard', 1.4);
      emitter.cameraImpulse('guard-break', 0.7, p.nx, 0.15, p.nz, f60(9), 20, 0.03);
    } else {
      emitter.cue(move.sfx.block || 'guard-light', p.cx, p.cy, p.cz,
        Math.min(1, 0.5 + move.damage / 60), 1, 0, 'guard');
      emitter.impact('guard-flash', p.cx, p.cy, p.cz, p.nx, p.ny, p.nz, 0.8, 'guard', 0.9);
      emitter.cameraImpulse('block', 0.12, p.nx, 0, p.nz, f60(3), 0, 0);
    }

    const bp = emitter.begin('combat:block');
    bp.attacker = attacker.index;
    bp.defender = defender.index;
    bp.moveId = move.id;
    bp.blockstun = move.blockstun;
    bp.chipDamage = chip;
    bp.pushback = move.pushbackBlock;
    bp.crouching = defender.crouching;
    bp.x = p.cx;
    bp.y = p.cy;
    bp.z = p.cz;
    bp.guardBreak = guardBreak;
    bp.guardMeter = defender.guardMeter / defender.guardMax;
    emitter.send('combat:block');

    logAction(attacker, ctx.time.tick, move.id, 'blocked');
    return move.damage >= 24 ? HITSTOP.medium : HITSTOP.light;
  }

  _applyArmor(ctx, emitter, attacker, defender, move, p) {
    attacker.moveHasHit = true;
    attacker.hitboxUsed[p.sourceIndex] = 1;
    defender.armorHitsLeft--;

    const absorbed = Math.max(1, Math.round(move.damage * defender.armorDamageMul));
    defender.health = Math.max(1, defender.health - absorbed);
    defender.damageTakenRound += absorbed;
    defender.perfectRound = false;
    addMeter(defender, computeMeterGain(defender, 'damage-taken', absorbed));

    const ap = emitter.begin('combat:armor');
    ap.fighter = defender.index;
    ap.moveId = move.id;
    ap.absorbedDamage = absorbed;
    ap.hitsRemaining = defender.armorHitsLeft;
    emitter.send('combat:armor');

    emitter.damage(defender.index, absorbed, defender.health, defender.maxHealth, move.id, false);
    emitter.cue('impact-medium', p.cx, p.cy, p.cz, 0.9, 0.82, 0, 'body');
    // Armor gets its own visual language so the player can see why their hit did nothing.
    emitter.impact('guard-flash', p.cx, p.cy, p.cz, p.nx, p.ny, p.nz, 1.1, 'guard', 1.15, 0.08);
    emitter.cameraImpulse('armor', 0.3, p.nx, 0.1, p.nz, f60(5), 14, 0);
    logAction(attacker, ctx.time.tick, move.id, 'armored');
    return HITSTOP.medium;
  }

  _applyThrow(ctx, emitter, attacker, defender, move, p) {
    attacker.moveHasHit = true;
    attacker.throwPartner = defender.index;
    attacker.throwMoveId = move.id;
    attacker.throwTick = 0;
    attacker.throwChainIndex = 0;

    defender.throwPartner = attacker.index;
    defender.throwBreakTicks = f60(Math.max(1, Math.round(move.tBlockstun / 2)) || 7);
    defender.throwBreakArmed = false;
    defender.move = null;
    defender.moveId = '';
    defender.hitstunTicks = 0;
    defender.blockstunTicks = 0;
    defender.parryTicks = 0;

    enterState(attacker, S.THROW_HOLD, ctx, emitter, move.id);
    enterState(defender, S.THROWN, ctx, emitter, move.id);

    const tp = emitter.begin('combat:throw');
    tp.attacker = attacker.index;
    tp.defender = defender.index;
    tp.moveId = move.id;
    tp.moveName = move.name;
    tp.damage = move.damage;
    tp.breakWindow = defender.throwBreakTicks;
    tp.chainIndex = 0;
    emitter.send('combat:throw');

    emitter.cue('throw-grab', p.cx, p.cy, p.cz, 1, 1, 0, 'body');
    emitter.impact('throw-burst', p.cx, p.cy, p.cz, p.nx, p.ny, p.nz, 0.9, 'body', 1);
    emitter.cinematic('throw', attacker.index, defender.index, f60(24), 'start');
    logAction(attacker, ctx.time.tick, move.id, 'throw');
    return HITSTOP.throw;
  }
}

/**
 * Can the defender block this move at their current guard height?
 * ARCHITECTURE.md §7.2 is the source of truth for this table.
 */
export function canBlock(defender, move) {
  const guarding = defender.guarding || defender.blockstunTicks > 0;
  if (!guarding) return false;
  if (defender.airborne) return false; // no air blocking in this game — jumping is a commitment
  switch (move.level) {
    case 'high':
      return !defender.crouching;
    case 'mid':
      return true;
    case 'special-mid':
      return !defender.crouching;
    case 'low':
      return defender.crouching;
    case 'unblockable':
    case 'throw':
      return false;
    default:
      return false;
  }
}

/** Parries work on strikes, not throws or unblockables. */
export function canParry(move) {
  return !move.isThrow && move.level !== 'unblockable';
}
